import { useTheme } from "../../../contexts/ThemeContext";

const features = [
  {
    tag: "AI Receptionist",
    tagColor: "#38BDF8",
    headline: "Every call answered.\nInstantly.",
    body: "Your AI receptionist picks up every inbound call and text in under 30 seconds — 24 hours a day, 7 days a week. No hold music. No voicemail. No missed revenue.",
    points: ["Answers calls & texts in real time", "Speaks in your brand's voice", "Handles multiple lines simultaneously", "Never calls in sick or takes a break"],
    pointColor: "#22C55E",
    image: "https://public.readdy.ai/ai/img_res/ceefcb40a3998e7654ecc796faa1d431.jpg",
    reverse: false,
  },
  {
    tag: "Lead Qualification Engine",
    tagColor: "#22C55E",
    headline: "Stop chasing leads.\nLet AI sort them.",
    body: "Every inquiry is scored and prioritized the moment it arrives. High-intent leads are escalated immediately. Low-quality traffic is filtered out automatically — so your team only touches what matters.",
    points: ["Scores every lead on intent signals", "Escalates hot leads instantly", "Filters spam & low-quality inquiries", "Feeds clean data into your CRM"],
    pointColor: "#F97316",
    image: "https://readdy.ai/api/search-image?query=modern%20dark%20UI%20dashboard%20showing%20AI%20lead%20scoring%20pipeline%20funnel%20analytics%20real-time%20data%20visualization%20glowing%20green%20orange%20metrics%20dark%20background%20professional%20SaaS%20interface%20cinematic&width=900&height=700&seq=feat-leads-v2-001&orientation=portrait",
    reverse: true,
  },
  {
    tag: "Smart Booking System",
    tagColor: "#F97316",
    headline: "Appointments booked.\nNo human required.",
    body: "The AI syncs with your real-time availability and books appointments directly into your calendar. Zero double-bookings, zero back-and-forth — just confirmed appointments on autopilot.",
    points: ["Syncs with your live calendar", "Sends automated confirmations", "Handles rescheduling & cancellations", "Works across all time zones"],
    pointColor: "#38BDF8",
    image: "https://readdy.ai/api/search-image?query=elegant%20dark%20scheduling%20calendar%20app%20interface%20AI-powered%20appointment%20booking%20system%20clean%20UI%20dark%20mode%20blue%20orange%20highlights%20modern%20dashboard%20booking%20confirmation%20slots%20grid%20professional%20minimal%20cinematic&width=900&height=700&seq=feat-booking-v2-001&orientation=portrait",
    reverse: false,
  },
  {
    tag: "Follow-Up Engine",
    tagColor: "#38BDF8",
    headline: "No lead left\nbehind. Ever.",
    body: "Missed calls, ghosted texts, and past customers are automatically re-engaged through intelligent follow-up sequences. Every touchpoint is timed, personalized, and conversion-focused.",
    points: ["Automated SMS & email sequences", "Re-engages cold & past leads", "Customizable follow-up cadences", "Tracks and reports conversion rates"],
    pointColor: "#22C55E",
    image: "https://readdy.ai/api/search-image?query=dark%20automation%20workflow%20builder%20interface%20glowing%20green%20orange%20nodes%20connected%20arrows%20email%20SMS%20sequence%20automation%20marketing%20funnel%20visualization%20dark%20background%20professional%20tech%20UI%20cinematic&width=900&height=700&seq=feat-followup-v2-001&orientation=portrait",
    reverse: true,
  },
];

export default function HowItWorks() {
  const { isDark } = useTheme();

  const sectionBg = isDark ? "bg-black" : "bg-white";
  const titleColor = isDark ? "text-white" : "text-[#1d1d1f]";
  const subtitleColor = isDark ? "text-white/35" : "text-black/30";
  const bodyColor = isDark ? "text-white/50" : "text-[#6e6e73]";
  const pointColor = isDark ? "text-white/70" : "text-[#3a3a3c]";
  const borderColor = isDark ? "border-white/[0.07]" : "border-black/[0.07]";
  const problemBg = isDark ? "from-black/90 via-black/60 to-black/30" : "from-black/92 via-black/65 to-black/35";

  return (
    <section id="how-it-works" className={`${sectionBg} py-24 md:py-32 transition-colors duration-300`}>
      <div className="text-center px-6 mb-24">
        <p className="text-[#38BDF8] text-[11px] font-semibold tracking-[0.2em] uppercase mb-5">How It Works</p>
        <h2 className={`text-[42px] md:text-[60px] font-black ${titleColor} tracking-[-0.03em] leading-tight`}>
          Your AI Workforce.<br />
          <span className={subtitleColor}>Four systems. One mission.</span>
        </h2>
      </div>

      <div className="flex flex-col gap-0">
        {features.map((feat, i) => (
          <div key={i} className={`border-t ${borderColor} py-20 md:py-28 px-6 md:px-16 max-w-[1280px] mx-auto w-full`}>
            <div className={`flex flex-col ${feat.reverse ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-12 md:gap-20`}>
              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-semibold tracking-[0.2em] uppercase block mb-5" style={{ color: feat.tagColor }}>
                  {feat.tag}
                </span>
                <h3
                  className={`text-[36px] md:text-[52px] font-black ${titleColor} leading-[1.05] tracking-[-0.025em] mb-6`}
                  style={{ whiteSpace: "pre-line" }}
                >
                  {feat.headline}
                </h3>
                <p className={`${bodyColor} text-[16px] leading-[1.75] mb-8 max-w-md`}>{feat.body}</p>
                <ul className="flex flex-col gap-3">
                  {feat.points.map((pt, j) => (
                    <li key={j} className={`flex items-center gap-3 ${pointColor} text-[14px]`}>
                      <span className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-sm" style={{ color: feat.pointColor }}></i>
                      </span>
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex-1 min-w-0 w-full">
                <div className="relative rounded-[24px] overflow-hidden" style={{ aspectRatio: "9/7" }}>
                  <img src={feat.image} alt={feat.tag} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Problem banner — always cinematic dark */}
      <div className="mt-10 mx-6 md:mx-16 max-w-[1280px] xl:mx-auto rounded-[28px] overflow-hidden relative">
        <img
          src="https://readdy.ai/api/search-image?query=dramatic%20dark%20abstract%20business%20owner%20missing%20phone%20calls%20stress%20leads%20falling%20through%20gaps%20revenue%20lost%20missed%20opportunities%20dark%20moody%20cinematic%20professional%20wide%20angle&width=1440&height=500&seq=problem-banner-001&orientation=landscape"
          alt="The Problem"
          className="w-full h-[360px] md:h-[420px] object-cover object-center"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${problemBg}`}></div>
        <div className="absolute inset-0 flex items-center px-10 md:px-16">
          <div className="max-w-xl">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: "#F97316" }}>The Root Problem</p>
            <h3 className="text-[32px] md:text-[46px] font-black text-white leading-tight tracking-[-0.025em] mb-4">
              Most businesses don&apos;t have a lead problem.<br />
              <span className="text-white/45">They have a response problem.</span>
            </h3>
            <p className="text-white/55 text-[15px] leading-relaxed">
              The business that responds first wins. Every time. AI Talent Launch ensures that&apos;s always you.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
